import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Button,
  Dropdown,
  Form,
  Icon,
  Loader,
  Message,
  Modal,
  Table,
} from "semantic-ui-react";

import { axiosBasic } from "../../../../../api/axios";
import { config } from "../../../../../api/endPoints";
import * as projectActions from "../../../../domain/project/projectActions";
import * as teamActions from "../../../../domain/team/teamActions";
import { Common } from "../../../../../utils";
import { useSubscriptionStatus } from "../../../../../hooks/useSubscriptionStatus";
import SubscriptionRestriction from "../../../subscriptionRestriction/SubscriptionRestriction";
import CommonPageHeader from "../../../ui/page-header/CommonPageHeader";
import {
  parseProjectNotes,
  WORKFLOW_STATUS,
  INITIAL_TEAM_MODE,
  getProjectDisplayTitle,
  priorityKeyFromApi,
  getWorkflowStatusForForm,
} from "./projectFormMeta";
import "../departments/department.css";
import "./project-form.css";
import "../../../../domain/documents/Documents.css";

const { REST_API } = config;
const { Department, AppUser, Team } = REST_API;

/** Persists edit target when the URL has no id (refresh on `/projects/edit`). */
const EDIT_PROJECT_ID_STORAGE_KEY = "hrm_edit_project_id";

function clearEditProjectIdStorage() {
  try {
    sessionStorage.removeItem(EDIT_PROJECT_ID_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

const NAME_MAX_LENGTH = 250;
const PURPOSE_MAX_LENGTH = 500;
const DESCRIPTION_MAX_LENGTH = 2000;

const SUPERVISOR_ROLES = new Set([1, 2, 3]);

function normId(row) {
  if (!row || typeof row !== "object") return undefined;
  return row.id ?? row.Id;
}

/** Aligns with `userTeams.userId` / form multi-select (numeric or GUID string). */
function normalizeMemberId(raw) {
  if (raw == null || raw === "") return null;
  const s = String(raw).trim();
  const n = Number(s);
  if (Number.isFinite(n) && s === String(n)) return n;
  return s;
}

function normUser(u) {
  const primary =
    u.userId ??
    u.UserId ??
    u.appUserId ??
    u.AppUserId ??
    u.applicationUserId ??
    u.ApplicationUserId;
  const id =
    primary != null && primary !== "" ? normalizeMemberId(primary) : normalizeMemberId(normId(u));
  const name = u.name ?? u.Name ?? "";
  const email = u.email ?? u.Email ?? "";
  const role = u.role ?? u.Role;
  const teamRaw = u.teamId ?? u.TeamId ?? u.team?.id ?? u.team?.Id;
  const teamId =
    teamRaw != null && teamRaw !== "" ? projectActions.normalizeTeamIdForPayload(teamRaw) : null;
  return {
    id,
    name,
    email,
    role,
    roleName: u.roleName ?? u.RoleName,
    teamId,
  };
}

/** Member user ids returned on team list/detail DTOs (shape varies by API version). */
function extractTeamMemberIds(row) {
  if (!row || typeof row !== "object") return [];
  const tryArrays = [
    row.userIds,
    row.UserIds,
    row.appUserIds,
    row.AppUserIds,
    row.memberUserIds,
    row.MemberUserIds,
    row.employeeUserIds,
    row.EmployeeUserIds,
  ];
  for (const raw of tryArrays) {
    if (Array.isArray(raw) && raw.length) {
      return raw.map((x) => normalizeMemberId(x)).filter((x) => x != null);
    }
  }
  const nested = row.users ?? row.Users ?? row.members ?? row.Members ?? row.employees ?? row.Employees;
  if (Array.isArray(nested)) {
    return nested
      .map((item) => {
        if (item == null || typeof item !== "object") return null;
        const uid = item.id ?? item.Id ?? item.userId ?? item.UserId ?? item.appUserId ?? item.AppUserId;
        return uid != null ? normalizeMemberId(uid) : null;
      })
      .filter((x) => x != null);
  }
  return [];
}

/** List endpoints may return camelCase or PascalCase (`data` / `Data`, `success` / `Success`). */
function unwrapApiArray(res) {
  const body = res?.data;
  if (!body || typeof body !== "object") return [];
  const ok = body.success === true || body.Success === true;
  if (!ok) return [];
  const raw = body.data ?? body.Data;
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === "object") {
    if (Array.isArray(raw.items)) return raw.items;
    if (Array.isArray(raw.Items)) return raw.Items;
  }
  if (Array.isArray(body.users)) return body.users;
  if (Array.isArray(body.Users)) return body.Users;
  return [];
}

function normTeam(row) {
  if (!row || typeof row !== "object") return null;
  const idRaw =
    row.id ?? row.Id ?? row.ID ?? row.teamId ?? row.TeamId ?? row.teamID ?? row.TeamID;
  const name = row.name ?? row.Name ?? "";
  const isActive = row.isActive !== undefined ? row.isActive : row.IsActive;
  if (idRaw == null || idRaw === "") return null;
  const id = projectActions.normalizeTeamIdForPayload(idRaw);
  if (id == null) return null;
  const memberUserIds = extractTeamMemberIds(row);
  return { id, name, isActive, memberUserIds };
}

function toDateInputValue(iso) {
  if (!iso) return "";
  const s = String(iso);
  if (s.length >= 10 && s[4] === "-") return s.slice(0, 10);
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * API `NewTeamMemberUserIds`: string user ids to add to the team record.
 * - Existing team: initial members chosen who are not on the team's current server roster.
 * - New team (or no linked team): every selected initial member is new to that team.
 */
function computeNewTeamMemberUserIds(initialTeamMode, teamUserIds, rosterBaselineIds) {
  const selected = Array.isArray(teamUserIds) ? teamUserIds : [];
  const str = (x) => String(x);
  if (initialTeamMode === INITIAL_TEAM_MODE.EXISTING) {
    const roster = new Set((rosterBaselineIds || []).map(str));
    return selected.filter((uid) => !roster.has(str(uid))).map(str);
  }
  if (initialTeamMode === INITIAL_TEAM_MODE.NEW || initialTeamMode === INITIAL_TEAM_MODE.NONE) {
    return selected.map(str);
  }
  return [];
}

function mergeRosterBaselines(teamIds, rosterFn) {
  const seen = new Set();
  const out = [];
  for (const tid of teamIds || []) {
    for (const uid of rosterFn(tid)) {
      const k = String(uid);
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(uid);
    }
  }
  return out;
}

/**
 * Same as mergeRosterBaselines but uses GET Team/UserTeams cache per team when loaded successfully
 * so `newTeamMemberUserIds` matches server roster, not only client-side team list / user.teamId.
 */
function mergeRosterBaselinesWithUserTeamsCache(teamIds, userTeamsCache, rosterFn) {
  const seen = new Set();
  const out = [];
  const push = (uid) => {
    if (uid == null || uid === "") return;
    const k = String(uid);
    if (seen.has(k)) return;
    seen.add(k);
    out.push(uid);
  };
  for (const tid of teamIds || []) {
    const key = String(tid);
    const c = userTeamsCache[key];
    const loadedOk = c && !c.loading && c.error == null;
    if (loadedOk) {
      for (const row of c.rows || []) {
        const uid = normalizeMemberId(row.userId);
        if (uid != null) push(uid);
      }
    } else {
      for (const uid of rosterFn(tid)) push(uid);
    }
  }
  return out;
}

/** Dedupe user ids (GUID or number) preserving first occurrence order. */
function mergeUniqueUserIds(...lists) {
  const seen = new Set();
  const out = [];
  for (const list of lists) {
    if (!Array.isArray(list)) continue;
    for (const uid of list) {
      if (uid == null || uid === "") continue;
      const k = String(uid);
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(uid);
    }
  }
  return out;
}

function newTeamSlotRowKey() {
  return `ts-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function createEmptyTeamSlot() {
  return { rowKey: newTeamSlotRowKey(), teamId: null, additionalMemberUserIds: [] };
}

/** Snapshot linked-team rows + merged member ids when leaving EXISTING mode so we can restore after NEW/NONE. */
function snapshotExistingTeamState(formLike) {
  const slots = formLike.existingTeamSlots || [];
  return {
    existingTeamSlots: slots.map((s) => ({
      rowKey: s.rowKey,
      teamId: s.teamId,
      additionalMemberUserIds: Array.isArray(s.additionalMemberUserIds)
        ? [...s.additionalMemberUserIds]
        : [],
    })),
    existingTeamIds: [...(formLike.existingTeamIds || [])],
    teamUserIds: [...(formLike.teamUserIds || [])],
  };
}

/** Full project member ids from linked teams: each slot’s roster ∪ additional picks. */
function mergeSlotsToTeamUserIds(slots, rosterFn) {
  const seen = new Set();
  const out = [];
  const push = (uid) => {
    if (uid == null || uid === "") return;
    const k = String(uid);
    if (seen.has(k)) return;
    seen.add(k);
    out.push(uid);
  };
  for (const slot of slots || []) {
    if (!slot || !slot.teamId) continue;
    for (const uid of rosterFn(slot.teamId)) push(uid);
    for (const uid of slot.additionalMemberUserIds || []) push(uid);
  }
  return out;
}

function deriveExistingTeamIdsFromSlots(slots) {
  return (slots || [])
    .map((s) => s.teamId)
    .filter((id) => id != null && id !== "")
    .map((id) => projectActions.normalizeTeamIdForPayload(id))
    .filter((x) => x != null);
}

function hasLinkedTeamInSlots(slots) {
  return (slots || []).some(
    (s) =>
      s &&
      s.teamId != null &&
      s.teamId !== "" &&
      projectActions.normalizeTeamIdForPayload(s.teamId) != null
  );
}

/** GET Team/UserTeams row (camel or Pascal). */
function normProjectUserTeamItem(row) {
  if (!row || typeof row !== "object") return null;
  const id = row.id ?? row.Id;
  const userId = row.userId ?? row.UserId ?? "";
  return {
    key: `${id}-${userId}`,
    memberName: row.memberName ?? row.MemberName ?? "",
    userEmail: row.userEmail ?? row.UserEmail ?? "",
    userId,
  };
}

/** API may return multiple membership rows per user (e.g. different Ids); roster UI should list each person once. */
function dedupeProjectUserTeamRows(rows) {
  const seen = new Set();
  const out = [];
  for (const row of rows || []) {
    if (!row) continue;
    const uid = String(row.userId ?? "").trim();
    const email = String(row.userEmail ?? "").trim().toLowerCase();
    const dedupeKey = uid ? `u:${uid}` : email ? `e:${email}` : null;
    if (dedupeKey) {
      if (seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);
      out.push({ ...row, key: uid || email });
    } else {
      out.push(row);
    }
  }
  return out;
}

function mapUserTeamsToRosterRows(rawRows) {
  return dedupeProjectUserTeamRows(
    (rawRows || []).map(normProjectUserTeamItem).filter(Boolean)
  );
}

const emptyForm = () => ({
  name: "",
  priority: "medium",
  workflowStatus: WORKFLOW_STATUS.IN_PROGRESS,
  departmentId: "",
  clientName: "",
  technology: "",
  purpose: "",
  description: "",
  startDate: "",
  endDate: "",
  supervisorUserId: "",
  teamUserIds: [],
  initialTeamMode: INITIAL_TEAM_MODE.NONE,
  existingTeamIds: [],
  /** EXISTING mode: one row per linked team — team pick, additional members, own View roster. */
  existingTeamSlots: [],
  newTeamName: "",
  isInternal: false,
});

function normalizeSupervisorFormValue(raw) {
  if (raw == null || raw === "") return "";
  const s = String(raw).trim();
  const n = Number(s);
  if (Number.isFinite(n) && s === String(n)) return n;
  return s;
}

/** Resolve app user id from a ProjectTeamAssignment.userTeams row (shape varies by API). */
function pickUserIdFromUserTeamRow(row) {
  if (!row || typeof row !== "object") return null;
  const direct =
    row.appUserId ??
    row.AppUserId ??
    row.userId ??
    row.UserId ??
    row.memberUserId ??
    row.MemberUserId ??
    row.employeeUserId ??
    row.EmployeeUserId;
  if (direct != null && direct !== "") return direct;
  const u = row.user ?? row.User ?? row.appUser ?? row.AppUser ?? row.employee ?? row.Employee;
  if (u && typeof u === "object") {
    return u.id ?? u.Id ?? u.appUserId ?? u.AppUserId ?? u.userId ?? u.UserId;
  }
  return null;
}

function normalizeFormUserId(raw) {
  return normalizeMemberId(raw);
}

/**
 * GET-by-id payloads may include `projectTeamAssignments` with linked `teams` and `userTeams`
 * instead of (or in addition to) notes meta. Some APIs nest teams under each assignment; others
 * return `teams` / `userTeams` on the same envelope as `project` (merged onto the row in fetchProjectById).
 * `apiEmptyLink`: API returned team-related arrays but no resolvable team/member ids — EXISTING mode + "no linked team" UI.
 */
function extractTeamStateFromProjectDto(projectRow) {
  const rawAsg = projectRow.projectTeamAssignments ?? projectRow.ProjectTeamAssignments;
  const assignments = Array.isArray(rawAsg) ? rawAsg : [];
  const topTeams = projectRow.teams ?? projectRow.Teams;
  const topUserTeams = projectRow.userTeams ?? projectRow.UserTeams;
  const hadAssignmentArray = Array.isArray(rawAsg);
  const fromApi =
    hadAssignmentArray ||
    (Array.isArray(topTeams) && topTeams.length > 0) ||
    (Array.isArray(topUserTeams) && topUserTeams.length > 0);

  if (!fromApi) {
    return {
      existingTeamIds: [],
      teamUserIds: [],
      fromApi: false,
      apiEmptyLink: false,
    };
  }

  const teamKeys = new Set();
  const userIds = [];
  const userSeen = new Set();

  const addTeamRow = (t) => {
    if (!t || typeof t !== "object") return;
    const tid = t.id ?? t.Id;
    const norm = projectActions.normalizeTeamIdForPayload(tid);
    if (norm != null) teamKeys.add(String(norm));
  };

  const addUserTeamRow = (ut) => {
    const uidRaw = pickUserIdFromUserTeamRow(ut);
    const uid = normalizeFormUserId(uidRaw);
    if (uid == null || uid === "") return;
    const key = String(uid);
    if (userSeen.has(key)) return;
    userSeen.add(key);
    userIds.push(uid);
  };

  for (const asg of assignments) {
    if (!asg || typeof asg !== "object") continue;
    const teamsArr = asg.teams ?? asg.Teams;
    const teamList = Array.isArray(teamsArr) ? teamsArr : teamsArr ? [teamsArr] : [];
    for (const t of teamList) addTeamRow(t);
    const utArr = asg.userTeams ?? asg.UserTeams;
    const utList = Array.isArray(utArr) ? utArr : utArr ? [utArr] : [];
    for (const ut of utList) addUserTeamRow(ut);
  }

  if (Array.isArray(topTeams)) {
    for (const t of topTeams) addTeamRow(t);
  }
  if (Array.isArray(topUserTeams)) {
    for (const ut of topUserTeams) addUserTeamRow(ut);
  }

  const existingTeamIds = Array.from(teamKeys)
    .map((k) => {
      const n = Number(k);
      return Number.isFinite(n) && k === String(n) ? n : k;
    })
    .filter((x) => x != null && x !== "");

  const apiEmptyLink = existingTeamIds.length === 0 && userIds.length === 0;
  return {
    existingTeamIds,
    teamUserIds: userIds,
    fromApi: true,
    apiEmptyLink,
  };
}

function buildFormStateFromProjectDto(projectRow, limits) {
  const { PURPOSE_MAX_LENGTH, DESCRIPTION_MAX_LENGTH, NAME_MAX_LENGTH } = limits;
  const { purpose: purposeFromNotes, meta: metaFromNotes } = parseProjectNotes(
    projectRow.notes ?? projectRow.Notes ?? ""
  );
  const pit = metaFromNotes.projectInitialTeam ?? metaFromNotes.ProjectInitialTeam;
  const purpose = String(projectRow.purpose ?? projectRow.Purpose ?? purposeFromNotes ?? "").slice(
    0,
    PURPOSE_MAX_LENGTH
  );

  const pk = priorityKeyFromApi(projectRow.priority ?? projectRow.Priority);
  const metaPriority = metaFromNotes.priority;
  const priority =
    pk ??
    (["low", "medium", "high", "critical"].includes(metaPriority) ? metaPriority : "medium");

  const teamRaw = metaFromNotes.teamUserIds;
  let teamIds = Array.isArray(teamRaw)
    ? teamRaw.map((x) => normalizeMemberId(x)).filter((x) => x != null)
    : [];
  const rawMetaTeamIds =
    projectRow.teamIds ??
    projectRow.TeamIds ??
    metaFromNotes.teamIds ??
    metaFromNotes.TeamIds ??
    (Array.isArray(pit?.teamIds) ? pit.teamIds : null);
  const rawMetaTeamId =
    projectRow.teamId ?? projectRow.TeamId ?? metaFromNotes.teamId ?? metaFromNotes.TeamId;
  let existingTeamIds = [];
  if (Array.isArray(rawMetaTeamIds) && rawMetaTeamIds.length) {
    existingTeamIds = rawMetaTeamIds
      .map((x) => projectActions.normalizeTeamIdForPayload(x))
      .filter((x) => x != null);
  } else if (rawMetaTeamId != null && rawMetaTeamId !== "") {
    const one = projectActions.normalizeTeamIdForPayload(rawMetaTeamId);
    if (one != null) existingTeamIds = [one];
  }
  const hasMetaTeam = existingTeamIds.length > 0;
  let initialTeamMode =
    metaFromNotes.initialTeamMode ?? pit?.initialTeamMode ?? pit?.InitialTeamMode;
  if (!initialTeamMode) {
    if ((metaFromNotes.newTeamName || "").trim()) initialTeamMode = INITIAL_TEAM_MODE.NEW;
    else if (hasMetaTeam) initialTeamMode = INITIAL_TEAM_MODE.EXISTING;
    else initialTeamMode = INITIAL_TEAM_MODE.NONE;
  }
  if (initialTeamMode !== INITIAL_TEAM_MODE.EXISTING) {
    existingTeamIds = [];
  }

  let existingTeamSlots = [];
  if (initialTeamMode === INITIAL_TEAM_MODE.EXISTING && existingTeamIds.length > 0) {
    existingTeamSlots = existingTeamIds.map((tid, i) => ({
      rowKey: `hydr-${i}-${String(tid)}`,
      teamId: tid,
      additionalMemberUserIds: [],
    }));
  }

  const fromAssignments = extractTeamStateFromProjectDto(projectRow);
  /** Notes explicitly "no linked team" while GET has empty teams/userTeams — keep NONE, do not force EXISTING. */
  const explicitNoLinkedTeamInNotes =
    metaFromNotes.initialTeamMode === INITIAL_TEAM_MODE.NONE ||
    pit?.initialTeamMode === INITIAL_TEAM_MODE.NONE ||
    pit?.InitialTeamMode === INITIAL_TEAM_MODE.NONE;

  if (fromAssignments.fromApi) {
    if (fromAssignments.existingTeamIds.length > 0) {
      existingTeamIds = fromAssignments.existingTeamIds;
      initialTeamMode = INITIAL_TEAM_MODE.EXISTING;
      if (fromAssignments.teamUserIds.length > 0) {
        teamIds = fromAssignments.teamUserIds;
      }
      existingTeamSlots = existingTeamIds.map((tid, i) => ({
        rowKey: `hydr-${i}-${String(tid)}`,
        teamId: tid,
        additionalMemberUserIds: [],
      }));
    } else if (fromAssignments.apiEmptyLink && !explicitNoLinkedTeamInNotes) {
      /** Empty assignments on the wire but meta did not say "no linked team" → EXISTING + empty row */
      existingTeamIds = [];
      teamIds = [];
      existingTeamSlots = [createEmptyTeamSlot()];
      initialTeamMode = INITIAL_TEAM_MODE.EXISTING;
    }
  }

  const slotsFromMeta = pit?.existingTeamSlots ?? pit?.ExistingTeamSlots;
  if (initialTeamMode === INITIAL_TEAM_MODE.EXISTING && Array.isArray(slotsFromMeta) && slotsFromMeta.length > 0) {
    const hasTeamInMeta = slotsFromMeta.some(
      (s) => s && (s.teamId != null || s.TeamId != null) && projectActions.normalizeTeamIdForPayload(s.teamId ?? s.TeamId) != null
    );
    if (hasTeamInMeta && existingTeamSlots.length === 0) {
      existingTeamSlots = slotsFromMeta.map((s, i) => ({
        rowKey: s.rowKey || `hydr-pit-${i}-${String(s.teamId ?? s.TeamId ?? i)}`,
        teamId: projectActions.normalizeTeamIdForPayload(s.teamId ?? s.TeamId),
        additionalMemberUserIds: Array.isArray(s.additionalMemberUserIds)
          ? s.additionalMemberUserIds.map((x) => normalizeMemberId(x)).filter((x) => x != null)
          : [],
      }));
      existingTeamIds = deriveExistingTeamIdsFromSlots(existingTeamSlots);
    } else if (existingTeamSlots.length > 0) {
      const byTeam = Object.fromEntries(
        slotsFromMeta
          .filter((s) => s && (s.teamId != null || s.TeamId != null))
          .map((s) => {
            const tid = projectActions.normalizeTeamIdForPayload(s.teamId ?? s.TeamId);
            return tid != null ? [String(tid), s] : null;
          })
          .filter(Boolean)
      );
      existingTeamSlots = existingTeamSlots.map((slot) => {
        const k = slot.teamId != null ? String(projectActions.normalizeTeamIdForPayload(slot.teamId)) : "";
        const m = k ? byTeam[k] : null;
        if (!m) return slot;
        const add = m.additionalMemberUserIds ?? m.AdditionalMemberUserIds;
        return {
          ...slot,
          additionalMemberUserIds: Array.isArray(add)
            ? add.map((x) => normalizeMemberId(x)).filter((x) => x != null)
            : slot.additionalMemberUserIds,
        };
      });
    }
  }

  return {
    name: (getProjectDisplayTitle(projectRow) || "").slice(0, NAME_MAX_LENGTH),
    priority,
    workflowStatus: getWorkflowStatusForForm(projectRow),
    departmentId:
      projectRow.departmentId != null && projectRow.departmentId !== ""
        ? Number(projectRow.departmentId)
        : projectRow.DepartmentId != null && projectRow.DepartmentId !== ""
          ? Number(projectRow.DepartmentId)
          : metaFromNotes.departmentId != null && metaFromNotes.departmentId !== ""
            ? Number(metaFromNotes.departmentId)
            : "",
    clientName: String(
      projectRow.clientName ?? projectRow.ClientName ?? metaFromNotes.clientName ?? ""
    ).trim(),
    technology: String(
      projectRow.technology ?? projectRow.Technology ?? metaFromNotes.technology ?? ""
    ).trim(),
    purpose,
    description: String(
      projectRow.description ?? projectRow.Description ?? metaFromNotes.description ?? ""
    ).slice(0, DESCRIPTION_MAX_LENGTH),
    startDate: toDateInputValue(
      projectRow.startDate ?? projectRow.StartDate ?? metaFromNotes.startDate
    ),
    endDate: toDateInputValue(
      projectRow.endDate ??
        projectRow.EndDate ??
        projectRow.expectedEndDate ??
        projectRow.ExpectedEndDate ??
        metaFromNotes.endDate
    ),
    supervisorUserId: normalizeSupervisorFormValue(
      projectRow.supervisorId ?? projectRow.SupervisorId ?? metaFromNotes.supervisorUserId
    ),
    teamUserIds: teamIds,
    initialTeamMode,
    existingTeamIds,
    existingTeamSlots,
    newTeamName: (metaFromNotes.newTeamName || "").slice(0, NAME_MAX_LENGTH),
    isInternal: !!(metaFromNotes.isInternal ?? projectRow.isInternal ?? projectRow.IsInternal),
  };
}

const ProjectFormPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { isSuspended } = useSubscriptionStatus();

  const isEditMode = location.pathname === "/settings/organization/projects/edit";
  const editIdStr = useMemo(() => {
    if (!isEditMode) return "";
    const fromState = location.state?.projectId ?? location.state?.id;
    if (fromState != null && String(fromState).trim() !== "") {
      return String(fromState).trim();
    }
    try {
      const stored = sessionStorage.getItem(EDIT_PROJECT_ID_STORAGE_KEY);
      return stored ? String(stored).trim() : "";
    } catch {
      return "";
    }
  }, [isEditMode, location.state, location.pathname]);

  const [form, setForm] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [teams, setTeams] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [bootstrapLoading, setBootstrapLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");
  const [projectMissing, setProjectMissing] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editProjectSource, setEditProjectSource] = useState(null);
  const hydratedRef = useRef(null);
  /** Avoid re-applying team roster to initial members unless the linked team ids changed. */
  const prevSyncedExistingTeamIdsRef = useRef(null);
  /** One-time distribution of API `teamUserIds` into per-slot `additionalMemberUserIds` on edit load. */
  const slotHydrationKeyRef = useRef(null);
  /** Edit: user chose "Use an existing team" with empty slot(s) — do not collapse to NONE on stray dropdown updates until a team is picked. */
  const emptyExistingUserChoiceRef = useRef(false);
  /** Last EXISTING-mode team slots + ids when user switched to NEW or NONE — restored when they pick EXISTING again. */
  const lastExistingTeamSnapshotRef = useRef(null);
  const prevDetailLoadingRef = useRef(false);

  const [teamRosterModalOpen, setTeamRosterModalOpen] = useState(false);
  /** Which team's roster the modal shows (GET Team/UserTeams/{id} data lives in teamUserTeamsCache). */
  const [teamRosterModalTeamId, setTeamRosterModalTeamId] = useState(null);
  /** Per team id: normalized UserTeams rows + load state (prefetched when a linked team is selected). */
  const [teamUserTeamsCache, setTeamUserTeamsCache] = useState({});
  const teamUserTeamsCacheRef = useRef({});
  useEffect(() => {
    teamUserTeamsCacheRef.current = teamUserTeamsCache;
  }, [teamUserTeamsCache]);

  const supervisorCandidates = useMemo(() => {
    return allUsers.filter((u) => u.id != null && SUPERVISOR_ROLES.has(Number(u.role)));
  }, [allUsers]);

  const departmentOptions = useMemo(() => {
    const opts = (departments || [])
      .map((d) => {
        const id = normId(d);
        const name = d.name ?? d.Name ?? "";
        if (id == null) return null;
        return { key: String(id), value: id, text: name };
      })
      .filter(Boolean);
    return [{ key: "none", value: "", text: t("app.projects.form.selectDepartment") }, ...opts];
  }, [departments, t]);

  const priorityOptions = useMemo(
    () => [
      { key: "low", value: "low", text: t("app.projects.form.priorityLow") },
      { key: "medium", value: "medium", text: t("app.projects.form.priorityMedium") },
      { key: "high", value: "high", text: t("app.projects.form.priorityHigh") },
      { key: "critical", value: "critical", text: t("app.projects.form.priorityCritical") },
    ],
    [t]
  );

  const workflowStatusOptions = useMemo(
    () => [
      { key: "ws-planning", value: WORKFLOW_STATUS.PLANNING, text: t("app.projects.workflowStatusPlanning") },
      { key: "ws-review", value: WORKFLOW_STATUS.REVIEW, text: t("app.projects.workflowStatusReview") },
      { key: "ws-onhold", value: WORKFLOW_STATUS.ON_HOLD, text: t("app.projects.workflowStatusOnHold") },
      { key: "ws-inprogress", value: WORKFLOW_STATUS.IN_PROGRESS, text: t("app.projects.workflowStatusInProgress") },
      { key: "ws-completed", value: WORKFLOW_STATUS.COMPLETED, text: t("app.projects.workflowStatusCompleted") },
      { key: "ws-inactive", value: WORKFLOW_STATUS.INACTIVE, text: t("app.projects.workflowStatusInactive") },
    ],
    [t]
  );

  const supervisorOptions = useMemo(() => {
    return supervisorCandidates.map((u) => ({
      key: String(u.id),
      value: u.id,
      text: u.email ? `${u.name} (${u.email})` : u.name,
    }));
  }, [supervisorCandidates]);

  const existingTeamOptions = useMemo(() => {
    const opts = [...teams]
      .sort((a, b) => String(a.name).localeCompare(String(b.name)))
      .map((row) => ({
        key: String(row.id),
        value: String(row.id),
        text: row.name || t("app.projects.form.unnamedTeam"),
      }));
    return [{ key: "none", value: "", text: t("app.projects.form.selectTeamPlaceholder") }, ...opts];
  }, [teams, t]);

  const hasTeams = teams.length > 0;

  /** Initial members block (single): NEW mode, or NONE with legacy saved ids. EXISTING uses per–team-slot UI. */
  const showInitialMembers = useMemo(() => {
    if (form.initialTeamMode === INITIAL_TEAM_MODE.EXISTING) {
      return false;
    }
    if (form.initialTeamMode === INITIAL_TEAM_MODE.NEW) {
      return true;
    }
    return Array.isArray(form.teamUserIds) && form.teamUserIds.length > 0;
  }, [form.initialTeamMode, form.teamUserIds]);

  const openTeamRosterModal = useCallback((teamId) => {
    const tid =
      teamId != null && teamId !== ""
        ? projectActions.normalizeTeamIdForPayload(teamId)
        : null;
    if (tid == null) return;
    const key = String(tid);
    setTeamRosterModalTeamId(tid);
    setTeamRosterModalOpen(true);

    const cur = teamUserTeamsCacheRef.current[key];
    if (cur && !cur.loading && cur.error == null && Array.isArray(cur.rows)) {
      return;
    }
    if (cur?.loading) {
      return;
    }
    setTeamUserTeamsCache((prev) => ({
      ...prev,
      [key]: { loading: true, rows: [], error: null },
    }));
    teamActions.fetchTeamUserTeams(tid).then((r) => {
      setTeamUserTeamsCache((prev) => ({
        ...prev,
        [key]: {
          loading: false,
          rows: mapUserTeamsToRosterRows(r.rows),
          error: r.error,
        },
      }));
    });
  }, []);

  /** Prefetch GET Team/UserTeams/{teamId} when a linked team is chosen so Initial members + View roster use the same list without an extra call on modal open. */
  useEffect(() => {
    if (form.initialTeamMode !== INITIAL_TEAM_MODE.EXISTING) return;
    const teamIds = [
      ...new Set(
        (form.existingTeamSlots || [])
          .map((s) => s.teamId)
          .map((id) => projectActions.normalizeTeamIdForPayload(id))
          .filter((id) => id != null && id !== "")
          .map((id) => String(id))
      ),
    ];
    if (teamIds.length === 0) return;
    (async () => {
      for (const tid of teamIds) {
        const cur = teamUserTeamsCacheRef.current[tid];
        if (cur && !cur.loading && cur.error == null && Array.isArray(cur.rows)) {
          continue;
        }
        if (cur?.loading) continue;
        setTeamUserTeamsCache((prev) => ({
          ...prev,
          [tid]: { loading: true, rows: [], error: null },
        }));
        const r = await teamActions.fetchTeamUserTeams(tid);
        /** Always apply result: Strict Mode re-runs effects and sets `cancelled`; skipping update left `loading: true` forever and blocked retries via `cur?.loading`. */
        setTeamUserTeamsCache((prev) => ({
          ...prev,
          [tid]: {
            loading: false,
            rows: mapUserTeamsToRosterRows(r.rows),
            error: r.error,
          },
        }));
      }
    })();
  }, [form.existingTeamSlots, form.initialTeamMode]);

  const teamRosterSections = useMemo(() => {
    if (teamRosterModalTeamId == null) return [];
    const key = String(teamRosterModalTeamId);
    const c = teamUserTeamsCache[key];
    const trow = teams.find((x) => String(x.id) === key);
    return [
      {
        teamId: teamRosterModalTeamId,
        teamName: trow?.name || t("app.projects.form.unnamedTeam"),
        rows: c?.rows || [],
        fetchError: c?.error,
      },
    ];
  }, [teamRosterModalTeamId, teamUserTeamsCache, teams, t]);

  const teamRosterLoading = useMemo(() => {
    if (!teamRosterModalOpen || teamRosterModalTeamId == null) return false;
    const c = teamUserTeamsCache[String(teamRosterModalTeamId)];
    if (!c) return true;
    if (c.error) return false;
    return !!c.loading;
  }, [teamRosterModalOpen, teamRosterModalTeamId, teamUserTeamsCache]);

  useEffect(() => {
    if (bootstrapLoading) return;
    if (teams.length > 0) return;
    /** Do not clobber edit hydration: GET /Project/{id} may set EXISTING before `teams` loads. */
    if (isEditMode) return;
    setForm((prev) => {
      if (prev.initialTeamMode !== INITIAL_TEAM_MODE.EXISTING) return prev;
      return {
        ...prev,
        initialTeamMode: INITIAL_TEAM_MODE.NONE,
        existingTeamIds: [],
        existingTeamSlots: [],
        newTeamName: "",
        teamUserIds: [],
      };
    });
  }, [teams, bootstrapLoading, isEditMode]);

  useEffect(() => {
    if (isSuspended) history.replace("/settings/company?tab=billing");
  }, [isSuspended, history]);

  /** Remember project id for `/projects/edit` (no guid in URL) so refresh still works. */
  useEffect(() => {
    if (!isEditMode) return;
    const id = location.state?.projectId ?? location.state?.id;
    if (id != null && String(id).trim() !== "") {
      try {
        sessionStorage.setItem(EDIT_PROJECT_ID_STORAGE_KEY, String(id).trim());
      } catch {
        /* ignore */
      }
    }
  }, [isEditMode, location.state]);

  useEffect(() => {
    if (isSuspended) return;
    let cancelled = false;
    (async () => {
      setBootstrapLoading(true);
      setFetchError("");
      try {
        const depUrl = `${Department.Get}0/1/500`;
        const teamUrl = `${Team.Get}0/1/500`;
        const userUrl = `${AppUser.GetUsers}?officeId=0&departmentId=0&start=0&page=1&take=500&isActive=true`;
        const settled = await Promise.allSettled([
          axiosBasic.get(depUrl),
          axiosBasic.get(userUrl),
          axiosBasic.get(teamUrl),
        ]);
        if (cancelled) return;
        const depRes = settled[0].status === "fulfilled" ? settled[0].value : null;
        const userRes = settled[1].status === "fulfilled" ? settled[1].value : null;
        const teamRes = settled[2].status === "fulfilled" ? settled[2].value : null;
        setDepartments(unwrapApiArray(depRes));
        const raw = unwrapApiArray(userRes);
        setAllUsers(raw.map(normUser).filter((u) => u.id != null));
        const teamList = unwrapApiArray(teamRes).map(normTeam).filter(Boolean);
        setTeams(teamList.filter((row) => row.isActive !== false));
      } catch (e) {
        if (!cancelled) {
          setFetchError(t("app.projects.form.loadError"));
          setDepartments([]);
          setTeams([]);
          setAllUsers([]);
        }
      } finally {
        if (!cancelled) setBootstrapLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isSuspended, t]);

  useEffect(() => {
    if (!isEditMode || !editIdStr) {
      setEditProjectSource(null);
      setProjectMissing(false);
      hydratedRef.current = null;
      return;
    }
    if (bootstrapLoading) return;

    let cancelled = false;
    (async () => {
      setDetailLoading(true);
      setProjectMissing(false);
      try {
        const res = await projectActions.fetchProjectById(editIdStr);
        if (cancelled) return;
        if (!res.success || !res.data) {
          setProjectMissing(true);
          setEditProjectSource(null);
          setFetchError(
            typeof res.message === "string" && res.message ? res.message : t("app.projects.form.loadError")
          );
          return;
        }
        const row = res.data;
        setEditProjectSource(row);
        setFetchError("");
        const idKey = normId(row) != null ? String(normId(row)) : editIdStr;
        try {
          setForm(
            buildFormStateFromProjectDto(row, {
              PURPOSE_MAX_LENGTH,
              DESCRIPTION_MAX_LENGTH,
              NAME_MAX_LENGTH,
            })
          );
          setFormErrors({});
          hydratedRef.current = idKey;
          prevSyncedExistingTeamIdsRef.current = null;
          try {
            sessionStorage.setItem(EDIT_PROJECT_ID_STORAGE_KEY, idKey);
          } catch {
            /* ignore */
          }
        } catch (e) {
          console.error("ProjectFormPage: failed to map project DTO to form", e);
          setProjectMissing(true);
          setFetchError(t("app.projects.form.loadError"));
        }
      } finally {
        setDetailLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isEditMode, editIdStr, bootstrapLoading, t]);

  /** Edit only: after project detail loads, if mode is EXISTING but no row has a real team, align radio to "No linked team". */
  useEffect(() => {
    const prevLoading = prevDetailLoadingRef.current;
    prevDetailLoadingRef.current = detailLoading;
    if (!isEditMode || detailLoading || bootstrapLoading || !editProjectSource) return;
    if (!(prevLoading && !detailLoading)) return;
    setForm((prev) => {
      if (prev.initialTeamMode !== INITIAL_TEAM_MODE.EXISTING) return prev;
      if (hasLinkedTeamInSlots(prev.existingTeamSlots)) return prev;
      emptyExistingUserChoiceRef.current = false;
      return {
        ...prev,
        initialTeamMode: INITIAL_TEAM_MODE.NONE,
        existingTeamIds: [],
        existingTeamSlots: [],
        teamUserIds: [],
      };
    });
  }, [isEditMode, detailLoading, bootstrapLoading, editProjectSource]);

  const rosterUserIdsForTeam = useCallback(
    (teamKey) => {
      const key = String(teamKey);
      const trow = teams.find((x) => String(x.id) === key);
      if (trow && Array.isArray(trow.memberUserIds) && trow.memberUserIds.length > 0) {
        return [...trow.memberUserIds];
      }
      return allUsers
        .filter((u) => u.teamId != null && String(u.teamId) === key)
        .map((u) => u.id)
        .filter((id) => id != null);
    },
    [teams, allUsers]
  );

  const teamUserOptions = useMemo(() => {
    return allUsers
      .filter((u) => u.id != null)
      .map((u) => ({
        key: String(u.id),
        value: u.id,
        text: u.email ? `${u.name} (${u.email})` : u.name,
      }));
  }, [allUsers]);

  /** Dropdown options for Initial members on one row: exclude users already on that team (GET UserTeams when loaded, else client roster). */
  const memberOptionsForTeamId = useCallback(
    (teamId) => {
      if (teamId == null || teamId === "") return teamUserOptions;
      const key = String(teamId);
      const cached = teamUserTeamsCache[key];
      const loadedOk = cached && !cached.loading && cached.error == null;
      const hide = new Set();
      if (loadedOk) {
        for (const row of cached.rows || []) {
          const uid = normalizeMemberId(row.userId);
          if (uid != null) hide.add(String(uid));
        }
      } else {
        const roster = rosterUserIdsForTeam(teamId);
        roster.forEach((x) => hide.add(String(x)));
      }
      return teamUserOptions.filter((opt) => !hide.has(String(opt.value)));
    },
    [teamUserOptions, teamUserTeamsCache, rosterUserIdsForTeam]
  );

  /** Single-select team options for one row — teams picked on other rows are unavailable. */
  const teamOptionsForSlot = useCallback(
    (rowKey) => {
      const taken = new Set(
        (form.existingTeamSlots || [])
          .filter((s) => s.rowKey !== rowKey && s.teamId != null && s.teamId !== "")
          .map((s) => String(s.teamId))
      );
      const opts = [...teams]
        .filter((row) => !taken.has(String(row.id)))
        .sort((a, b) => String(a.name).localeCompare(String(b.name)))
        .map((row) => ({
          key: String(row.id),
          value: String(row.id),
          text: row.name || t("app.projects.form.unnamedTeam"),
        }));
      return [{ key: "none", value: "", text: t("app.projects.form.selectTeamPlaceholder") }, ...opts];
    },
    [teams, t, form.existingTeamSlots]
  );

  const updateTeamSlot = useCallback((rowKey, patch) => {
    setForm((prev) => {
      const slots = Array.isArray(prev.existingTeamSlots) ? [...prev.existingTeamSlots] : [];
      const idx = slots.findIndex((s) => s.rowKey === rowKey);
      if (idx < 0) return prev;
      const nextSlot = { ...slots[idx], ...patch };
      slots[idx] = nextSlot;
      const merged = mergeSlotsToTeamUserIds(slots, rosterUserIdsForTeam);
      const ids = deriveExistingTeamIdsFromSlots(slots);
      if (hasLinkedTeamInSlots(slots)) {
        emptyExistingUserChoiceRef.current = false;
      }
      if (
        isEditMode &&
        prev.initialTeamMode === INITIAL_TEAM_MODE.EXISTING &&
        !hasLinkedTeamInSlots(slots) &&
        !emptyExistingUserChoiceRef.current
      ) {
        return {
          ...prev,
          initialTeamMode: INITIAL_TEAM_MODE.NONE,
          existingTeamIds: [],
          existingTeamSlots: [],
          teamUserIds: [],
        };
      }
      return { ...prev, existingTeamSlots: slots, teamUserIds: merged, existingTeamIds: ids };
    });
  }, [rosterUserIdsForTeam, isEditMode]);

  const setSlotTeamId = useCallback(
    (rowKey, value) => {
      const raw = value === "" || value == null ? null : projectActions.normalizeTeamIdForPayload(value);
      updateTeamSlot(rowKey, {
        teamId: raw,
        additionalMemberUserIds: [],
      });
    },
    [updateTeamSlot]
  );

  const setSlotAdditionalMembers = useCallback(
    (rowKey, value) => {
      const next = Array.isArray(value) ? value : [];
      setForm((prev) => {
        const slots = Array.isArray(prev.existingTeamSlots) ? [...prev.existingTeamSlots] : [];
        const idx = slots.findIndex((s) => s.rowKey === rowKey);
        if (idx < 0) return prev;
        slots[idx] = { ...slots[idx], additionalMemberUserIds: next };
        const merged = mergeSlotsToTeamUserIds(slots, rosterUserIdsForTeam);
        const ids = deriveExistingTeamIdsFromSlots(slots);
        return { ...prev, existingTeamSlots: slots, teamUserIds: merged, existingTeamIds: ids };
      });
    },
    [rosterUserIdsForTeam]
  );

  const addTeamSlotRow = useCallback(() => {
    setForm((prev) => ({
      ...prev,
      existingTeamSlots: [...(prev.existingTeamSlots || []), createEmptyTeamSlot()],
    }));
  }, []);

  const removeTeamSlotRow = useCallback((rowKey) => {
    setForm((prev) => {
      const slots = (prev.existingTeamSlots || []).filter((s) => s.rowKey !== rowKey);
      const merged = mergeSlotsToTeamUserIds(slots, rosterUserIdsForTeam);
      const ids = deriveExistingTeamIdsFromSlots(slots);
      if (
        isEditMode &&
        prev.initialTeamMode === INITIAL_TEAM_MODE.EXISTING &&
        !hasLinkedTeamInSlots(slots)
      ) {
        emptyExistingUserChoiceRef.current = false;
        return {
          ...prev,
          initialTeamMode: INITIAL_TEAM_MODE.NONE,
          existingTeamIds: [],
          existingTeamSlots: [],
          teamUserIds: [],
        };
      }
      return { ...prev, existingTeamSlots: slots, teamUserIds: merged, existingTeamIds: ids };
    });
  }, [rosterUserIdsForTeam, isEditMode]);

  useEffect(() => {
    slotHydrationKeyRef.current = null;
    lastExistingTeamSnapshotRef.current = null;
  }, [editIdStr]);

  /** Edit load: put non-roster project members into the right slot’s `additionalMemberUserIds` using `userTeams`. */
  useEffect(() => {
    if (!isEditMode || !editProjectSource || bootstrapLoading) return;
    if (form.initialTeamMode !== INITIAL_TEAM_MODE.EXISTING) return;
    if (!teams.length) return;
    if (slotHydrationKeyRef.current === editIdStr) return;

    setForm((prev) => {
      const slots = prev.existingTeamSlots;
      if (!slots?.some((s) => s.teamId)) return prev;

      const row = editProjectSource;
      const uts = row.userTeams ?? row.UserTeams ?? [];
      const tids = deriveExistingTeamIdsFromSlots(slots);
      if (tids.length === 0) return prev;

      const unionRoster = mergeRosterBaselines(tids, rosterUserIdsForTeam);
      const rset = new Set(unionRoster.map((x) => String(x)));
      const extras = (prev.teamUserIds || []).filter((uid) => !rset.has(String(uid)));
      if (extras.length === 0) {
        slotHydrationKeyRef.current = editIdStr;
        return prev;
      }

      const nextSlots = slots.map((s) => ({
        ...s,
        additionalMemberUserIds: Array.isArray(s.additionalMemberUserIds) ? [...s.additionalMemberUserIds] : [],
      }));
      const byTid = Object.fromEntries(nextSlots.filter((s) => s.teamId).map((s) => [String(s.teamId), s]));

      for (const uid of extras) {
        let targetTid = null;
        const ut = uts.find((u) => String(pickUserIdFromUserTeamRow(u)) === String(uid));
        if (ut) targetTid = ut.teamId ?? ut.TeamId;
        if (targetTid == null || !byTid[String(targetTid)]) {
          targetTid = nextSlots.find((s) => s.teamId)?.teamId;
        }
        if (targetTid == null) continue;
        const slot = byTid[String(targetTid)];
        const k = String(uid);
        if (!slot.additionalMemberUserIds.some((x) => String(x) === k)) slot.additionalMemberUserIds.push(uid);
      }
      slotHydrationKeyRef.current = editIdStr;
      return { ...prev, existingTeamSlots: nextSlots };
    });
  }, [isEditMode, editProjectSource, editIdStr, bootstrapLoading, teams, form.initialTeamMode, rosterUserIdsForTeam]);

  const setField = useCallback((name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: undefined }));
  }, []);

  const onInitialMembersDropdownChange = useCallback(
    (e, { value }) => {
      setField("teamUserIds", Array.isArray(value) ? value : []);
    },
    [setField]
  );

  const setInitialTeamMode = useCallback((mode) => {
    if (mode !== INITIAL_TEAM_MODE.EXISTING) {
      emptyExistingUserChoiceRef.current = false;
    }
    setForm((prev) => {
      if (prev.initialTeamMode === INITIAL_TEAM_MODE.EXISTING && mode !== INITIAL_TEAM_MODE.EXISTING) {
        lastExistingTeamSnapshotRef.current = snapshotExistingTeamState(prev);
      }
      const next = { ...prev, initialTeamMode: mode };
      if (mode === INITIAL_TEAM_MODE.NONE) {
        next.existingTeamIds = [];
        next.existingTeamSlots = [];
        next.newTeamName = "";
        next.teamUserIds = [];
      } else if (mode === INITIAL_TEAM_MODE.EXISTING) {
        next.newTeamName = "";
        const snap = lastExistingTeamSnapshotRef.current;
        const canRestore =
          snap && Array.isArray(snap.existingTeamSlots) && snap.existingTeamSlots.length > 0;
        if (canRestore) {
          next.existingTeamSlots = snap.existingTeamSlots.map((s) => ({
            rowKey: s.rowKey,
            teamId: s.teamId,
            additionalMemberUserIds: Array.isArray(s.additionalMemberUserIds)
              ? [...s.additionalMemberUserIds]
              : [],
          }));
          next.existingTeamIds = [...(snap.existingTeamIds || [])];
          next.teamUserIds = [...(snap.teamUserIds || [])];
          emptyExistingUserChoiceRef.current = !hasLinkedTeamInSlots(next.existingTeamSlots);
        } else {
          next.existingTeamSlots = [createEmptyTeamSlot()];
          next.existingTeamIds = [];
          next.teamUserIds = [];
          emptyExistingUserChoiceRef.current = true;
        }
      } else if (mode === INITIAL_TEAM_MODE.NEW) {
        next.existingTeamIds = [];
        next.existingTeamSlots = [];
        /** Was "Use an existing team": `teamUserIds` holds merged roster + extras — do not carry into "Create a new team". */
        if (prev.initialTeamMode === INITIAL_TEAM_MODE.EXISTING) {
          next.teamUserIds = [];
        }
      }
      return next;
    });
    setFormErrors((prev) => ({
      ...prev,
      existingTeamIds: undefined,
      newTeamName: undefined,
    }));
  }, []);

  const validate = () => {
    const errors = {};
    if (!form.name.trim()) errors.name = t("app.projects.nameRequired");
    else if (form.name.length > NAME_MAX_LENGTH)
      errors.name = t("app.common.nameMaxChars", { max: NAME_MAX_LENGTH });
    if (form.purpose.length > PURPOSE_MAX_LENGTH)
      errors.purpose = t("app.projects.form.purposeMaxChars", { max: PURPOSE_MAX_LENGTH });
    if (form.description.length > DESCRIPTION_MAX_LENGTH)
      errors.description = t("app.projects.form.descriptionMaxChars", { max: DESCRIPTION_MAX_LENGTH });
    if (form.initialTeamMode === INITIAL_TEAM_MODE.EXISTING) {
      const ok = (form.existingTeamSlots || []).some(
        (s) => s.teamId != null && s.teamId !== "" && projectActions.normalizeTeamIdForPayload(s.teamId) != null
      );
      /** Edit: allow saving with no linked team (user can link later or switch to "Create a new team"). */
      if (!ok && !isEditMode) {
        errors.existingTeamIds = t("app.projects.form.existingTeamRequired");
      }
    }
    if (form.initialTeamMode === INITIAL_TEAM_MODE.NEW) {
      if (!form.newTeamName.trim()) {
        errors.newTeamName = t("app.projects.form.teamNameRequired");
      } else if (form.newTeamName.length > NAME_MAX_LENGTH) {
        errors.newTeamName = t("app.common.nameMaxChars", { max: NAME_MAX_LENGTH });
      }
    }
    if (!isEditMode) {
      if (form.supervisorUserId === "" || form.supervisorUserId == null) {
        errors.supervisorUserId = t("app.projects.form.supervisorRequired");
      }
    }
    if (
      form.startDate &&
      form.endDate &&
      String(form.endDate) < String(form.startDate)
    ) {
      errors.endDate = t("app.projects.form.endDateBeforeStart");
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const onStartDateChange = useCallback((value) => {
    setForm((prev) => {
      const next = { ...prev, startDate: value };
      if (value && prev.endDate && String(prev.endDate) < String(value)) {
        next.endDate = value;
      }
      return next;
    });
    setFormErrors((prev) => ({ ...prev, startDate: undefined, endDate: undefined }));
  }, []);

  const onEndDateChange = useCallback((value) => {
    setForm((prev) => {
      const next = { ...prev, endDate: value };
      if (value && prev.startDate && String(value) < String(prev.startDate)) {
        next.endDate = prev.startDate;
      }
      return next;
    });
    setFormErrors((prev) => ({ ...prev, startDate: undefined, endDate: undefined }));
  }, []);

  const buildPayload = () => ({
    name: form.name.trim(),
    purpose: form.purpose.trim(),
    description: form.description.trim(),
    priority: form.priority,
    workflowStatus: form.workflowStatus || WORKFLOW_STATUS.IN_PROGRESS,
    departmentId: form.departmentId === "" ? null : form.departmentId,
    clientName: form.clientName.trim(),
    technology: form.technology.trim(),
    startDate: form.startDate || null,
    endDate: form.endDate || null,
    supervisorUserId: form.supervisorUserId === "" ? null : form.supervisorUserId,
    teamUserIds: Array.isArray(form.teamUserIds) ? form.teamUserIds : [],
    isInternal: !!form.isInternal,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      /**
       * API requires at least one team when InitialTeamMode is `existing`.
       * Edit allows an empty linked list in the UI; treat that as `none` on save.
       */
      let effectiveInitialTeamMode = form.initialTeamMode;
      if (
        isEditMode &&
        form.initialTeamMode === INITIAL_TEAM_MODE.EXISTING &&
        !hasLinkedTeamInSlots(form.existingTeamSlots)
      ) {
        effectiveInitialTeamMode = INITIAL_TEAM_MODE.NONE;
      }

      let resolvedTeamId = null;
      let resolvedNewTeamName = "";

      if (effectiveInitialTeamMode === INITIAL_TEAM_MODE.NEW) {
        resolvedNewTeamName = form.newTeamName.trim();
        if (isEditMode && editProjectSource) {
          const { meta: prevMeta } = parseProjectNotes(editProjectSource.notes);
          const prevIds = projectActions.normalizeTeamIdsList({
            teamIds: prevMeta.teamIds ?? prevMeta.TeamIds,
            teamId: prevMeta.teamId ?? prevMeta.TeamId,
          });
          const prevIdNorm =
            prevIds.length > 0 ? projectActions.normalizeTeamIdForPayload(prevIds[0]) : null;
          const prevName = (prevMeta.newTeamName || "").trim();
          if (prevIdNorm != null && prevName === form.newTeamName.trim()) {
            resolvedTeamId = prevIdNorm;
          }
        }
      } else if (effectiveInitialTeamMode === INITIAL_TEAM_MODE.EXISTING) {
        const ids = (form.existingTeamIds || [])
          .map((x) => projectActions.normalizeTeamIdForPayload(x))
          .filter((x) => x != null);
        resolvedTeamId = ids[0] ?? null;
      }

      let resolvedTeamIds = [];
      if (effectiveInitialTeamMode === INITIAL_TEAM_MODE.EXISTING) {
        resolvedTeamIds = (form.existingTeamIds || [])
          .map((x) => projectActions.normalizeTeamIdForPayload(x))
          .filter((x) => x != null)
          .map((x) => String(x));
      } else if (effectiveInitialTeamMode === INITIAL_TEAM_MODE.NEW && resolvedTeamId != null) {
        resolvedTeamIds = [String(resolvedTeamId)];
      }

      const rosterBaseline =
        effectiveInitialTeamMode === INITIAL_TEAM_MODE.EXISTING && (form.existingTeamIds || []).length > 0
          ? mergeRosterBaselinesWithUserTeamsCache(
              form.existingTeamIds,
              teamUserTeamsCache,
              rosterUserIdsForTeam
            )
          : [];
      const newTeamMemberUserIds = computeNewTeamMemberUserIds(
        effectiveInitialTeamMode,
        form.teamUserIds,
        rosterBaseline
      );

      const base = buildPayload();
      const payload = {
        ...base,
        teamIds: resolvedTeamIds,
        initialTeamMode: effectiveInitialTeamMode,
        newTeamName: effectiveInitialTeamMode === INITIAL_TEAM_MODE.NEW ? resolvedNewTeamName : "",
        newTeamMemberUserIds,
        existingTeamSlots:
          effectiveInitialTeamMode === INITIAL_TEAM_MODE.EXISTING ? form.existingTeamSlots || [] : [],
      };

      let res;
      if (isEditMode && editProjectSource) {
        res = await dispatch(
          projectActions.patchProject({
            id: editProjectSource.id ?? editProjectSource.Id,
            name: payload.name,
            purpose: payload.purpose,
            description: payload.description,
            priority: payload.priority,
            workflowStatus: payload.workflowStatus,
            departmentId: payload.departmentId,
            clientName: payload.clientName,
            technology: payload.technology,
            startDate: payload.startDate,
            endDate: payload.endDate,
            supervisorUserId: payload.supervisorUserId,
            teamUserIds: payload.teamUserIds,
            teamIds: payload.teamIds,
            initialTeamMode: payload.initialTeamMode,
            newTeamName: payload.newTeamName,
            newTeamMemberUserIds: payload.newTeamMemberUserIds,
            existingTeamSlots: payload.existingTeamSlots,
            isInternal: payload.isInternal,
            isActive:
              editProjectSource.isActive !== undefined
                ? editProjectSource.isActive
                : editProjectSource.IsActive !== false,
          })
        );
      } else {
        res = await dispatch(projectActions.saveProject(payload));
      }
      if (res?.success) {
        Common.showSuccessMessage(
          res?.message ||
            (isEditMode ? t("app.projects.successUpdated") : t("app.projects.successCreated"))
        );
        clearEditProjectIdStorage();
        history.push("/settings/organization/projects");
      } else {
        Common.showErrorMessage(res?.message || t("app.projects.errorGeneric"));
      }
    } catch {
      Common.showErrorMessage(t("app.projects.errorGeneric"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const goBack = () => {
    clearEditProjectIdStorage();
    history.push("/settings/organization/projects");
  };

  if (isSuspended) {
    return (
      <div style={{ padding: "20px" }}>
        <SubscriptionRestriction actionKey="app.subscriptionRestriction.actionAccessSettings" />
      </div>
    );
  }

  const showLoader = bootstrapLoading || (isEditMode && detailLoading);
  const editMissing = isEditMode && !bootstrapLoading && !detailLoading && projectMissing;

  return (
    <div className="project-form-page documents-page container-fluid px-3 px-md-4 py-4">
      <CommonPageHeader
        barClassName="documents-header-bar project-form-documents-header"
        icon={<Icon name="briefcase" className="m-0" />}
        title={isEditMode ? t("app.projects.editTitle") : t("app.projects.createTitle")}
        description={t("app.projects.form.subtitle")}
        onBack={goBack}
        backLabel={t("app.projects.form.backToList")}
      />

      <div className="project-form-page-inner">
        {fetchError && (
          <Message negative className="project-form-error">
            <p>{fetchError}</p>
          </Message>
        )}

        {showLoader ? (
          <div className="project-form-loading">
            <Loader active inline="centered" size="large" />
          </div>
        ) : editMissing ? (
          <Message negative>
            <p>{t("app.projects.form.projectNotFound")}</p>
          </Message>
        ) : (
          <div className="project-form-shell">
            <div className="project-form-card">
              <form className="project-form-grid" onSubmit={handleSubmit} noValidate>
                <div className="project-form-section project-form-section--core project-form-span-12">
                  <h3 className="project-form-section-title">
                    <span className="project-form-section-title-icon" aria-hidden>
                      <Icon name="edit outline" />
                    </span>
                    {t("app.projects.form.sectionCore")}
                  </h3>

                <div className="project-form-row project-form-row--name-priority">
                  <div className="project-form-field project-form-field--grow">
                    <label htmlFor="pf-name">{t("app.projects.form.projectName")}</label>
                    <Form.Input
                      id="pf-name"
                      className="project-form-control"
                      value={form.name}
                      maxLength={NAME_MAX_LENGTH}
                      error={!!formErrors.name}
                      onChange={(e, { value }) => setField("name", value)}
                      placeholder={t("app.projects.enterName")}
                    />
                    {formErrors.name && (
                      <Message negative size="tiny" content={formErrors.name} />
                    )}
                  </div>
                  <div className="project-form-field project-form-field--priority">
                    <label htmlFor="pf-priority-ddl">{t("app.projects.form.priority")}</label>
                    <Dropdown
                      id="pf-priority-ddl"
                      selection
                      fluid
                      className="project-form-dropdown"
                      options={priorityOptions}
                      value={form.priority}
                      onChange={(e, { value }) => setField("priority", value)}
                    />
                  </div>
                </div>

                <div className="project-form-row project-form-row--two-col">
                  <div className="project-form-field project-form-field--half">
                    <label htmlFor="pf-dept-ddl">{t("app.projects.form.department")}</label>
                    <Dropdown
                      id="pf-dept-ddl"
                      selection
                      fluid
                      className="project-form-dropdown"
                      options={departmentOptions}
                      value={form.departmentId === "" ? "" : form.departmentId}
                      onChange={(e, { value }) => setField("departmentId", value === "" ? "" : Number(value))}
                    />
                  </div>
                  <div className="project-form-field project-form-field--half">
                    <label htmlFor="pf-workflow-status-ddl">{t("app.projects.form.workflowStatus")}</label>
                    <Dropdown
                      id="pf-workflow-status-ddl"
                      selection
                      fluid
                      className="project-form-dropdown"
                      options={workflowStatusOptions}
                      value={form.workflowStatus}
                      onChange={(e, { value }) => setField("workflowStatus", value)}
                    />
                  </div>
                </div>
                <div>
                  <p className="project-form-hint project-form-hint--below-row">{t("app.projects.form.workflowStatusHint")}</p>
                </div>

                <div className="project-form-field project-form-field--checkbox project-form-internal-panel">
                  <Form.Checkbox
                    id="pf-is-internal"
                    toggle={false}
                    label={t("app.projects.form.isInternal")}
                    checked={!!form.isInternal}
                    onChange={(e, { checked }) => setField("isInternal", !!checked)}
                  />
                  <p className="project-form-hint project-form-hint--below-row">{t("app.projects.form.isInternalHint")}</p>
                </div>

                <div className="project-form-row project-form-row--two-col">
                  <div className="project-form-field project-form-field--half">
                    <label htmlFor="pf-client">{t("app.projects.form.clientName")}</label>
                    <Form.Input
                      id="pf-client"
                      className="project-form-control"
                      value={form.clientName}
                      onChange={(e, { value }) => setField("clientName", value)}
                      placeholder={t("app.projects.form.clientPlaceholder")}
                    />
                  </div>
                  <div className="project-form-field project-form-field--half">
                    <label htmlFor="pf-tech">{t("app.projects.form.technology")}</label>
                    <Form.Input
                      id="pf-tech"
                      className="project-form-control"
                      value={form.technology}
                      onChange={(e, { value }) => setField("technology", value)}
                      placeholder={t("app.projects.form.technologyPlaceholder")}
                    />
                  </div>
                </div>

                <div className="project-form-field project-form-span-12">
                  <label htmlFor="pf-purpose">{t("app.projects.form.purpose")}</label>
                  <textarea
                    id="pf-purpose"
                    className="project-form-textarea"
                    rows={4}
                    maxLength={PURPOSE_MAX_LENGTH}
                    value={form.purpose}
                    onChange={(e) => setField("purpose", e.target.value)}
                    placeholder={t("app.projects.form.purposePlaceholder")}
                    aria-describedby="pf-purpose-count"
                  />
                  <div id="pf-purpose-count" className="project-form-char-count">
                    {t("app.common.characterCount", {
                      current: form.purpose.length,
                      max: PURPOSE_MAX_LENGTH,
                    })}
                  </div>
                  {formErrors.purpose && (
                    <Message negative size="tiny" content={formErrors.purpose} />
                  )}
                </div>

                <div className="project-form-field project-form-span-12">
                  <label htmlFor="pf-description">{t("app.projects.form.descriptionField")}</label>
                  <textarea
                    id="pf-description"
                    className="project-form-textarea"
                    rows={4}
                    maxLength={DESCRIPTION_MAX_LENGTH}
                    value={form.description}
                    onChange={(e) => setField("description", e.target.value)}
                    placeholder={t("app.projects.form.descriptionPlaceholder")}
                    aria-describedby="pf-description-count"
                  />
                  <div id="pf-description-count" className="project-form-char-count">
                    {t("app.common.characterCount", {
                      current: form.description.length,
                      max: DESCRIPTION_MAX_LENGTH,
                    })}
                  </div>
                  {formErrors.description && (
                    <Message negative size="tiny" content={formErrors.description} />
                  )}
                </div>
                </div>

                <div className="project-form-section project-form-section--timeline project-form-span-12">
                  <h3 className="project-form-section-title project-form-section-gap">
                    <span className="project-form-section-title-icon" aria-hidden>
                      <Icon name="calendar outline" />
                    </span>
                    {t("app.projects.form.sectionTimeline")}
                  </h3>

                <div className="project-form-row project-form-row--dates">
                  <div className="project-form-field project-form-field--date">
                    <label htmlFor="pf-start">{t("app.projects.form.startDate")}</label>
                    <Form.Input
                      id="pf-start"
                      className="project-form-control project-form-date-input"
                      type="date"
                      value={form.startDate}
                      max={form.endDate || undefined}
                      onChange={(e, { value }) => onStartDateChange(value)}
                    />
                  </div>
                  <div className="project-form-field project-form-field--date">
                    <label htmlFor="pf-end">{t("app.projects.form.endDate")}</label>
                    <Form.Input
                      id="pf-end"
                      className="project-form-control project-form-date-input"
                      type="date"
                      value={form.endDate}
                      min={form.startDate || undefined}
                      error={!!formErrors.endDate}
                      onChange={(e, { value }) => onEndDateChange(value)}
                    />
                    {formErrors.endDate && (
                      <Message negative size="tiny" content={formErrors.endDate} />
                    )}
                  </div>
                </div>

                <div className="project-form-field project-form-span-12">
                  <label htmlFor="pf-supervisor-ddl">{t("app.projects.form.projectSupervisor")}</label>
                  <div className="project-form-supervisor-wrap">
                    <div className="project-form-supervisor-icon" aria-hidden>
                      <Icon name="user outline" />
                    </div>
                    <Dropdown
                      id="pf-supervisor-ddl"
                      selection
                      fluid
                      search
                      className="project-form-dropdown project-form-dropdown--inset"
                      placeholder={t("app.projects.form.supervisorPlaceholder")}
                      options={supervisorOptions}
                      value={form.supervisorUserId === "" ? undefined : form.supervisorUserId}
                      onChange={(e, { value }) =>
                        setField("supervisorUserId", value === undefined || value === null ? "" : value)
                      }
                      error={!!formErrors.supervisorUserId}
                    />
                  </div>
                  {formErrors.supervisorUserId && (
                    <Message negative size="tiny" content={formErrors.supervisorUserId} />
                  )}
                </div>
                </div>

                <div className="project-form-section project-form-section--team project-form-span-12">
                  <h3 className="project-form-section-title project-form-section-gap">
                    <span className="project-form-section-title-icon" aria-hidden>
                      <Icon name="users" />
                    </span>
                    {t("app.projects.form.sectionTeam")}
                  </h3>

                <p className="project-form-hint project-form-hint--below-row project-form-team-intro">
                  {t("app.projects.form.teamLinkHint")}
                </p>

                <div className="project-form-field project-form-team-mode">
                  <span className="project-form-label-block" id="pf-team-mode-label">
                    {t("app.projects.form.initialTeam")}
                  </span>
                  <Form.Group className="project-form-team-mode-radios" role="radiogroup" aria-labelledby="pf-team-mode-label">
                    <Form.Radio
                      label={t("app.projects.form.teamModeNone")}
                      name="initialTeamMode"
                      value={INITIAL_TEAM_MODE.NONE}
                      checked={form.initialTeamMode === INITIAL_TEAM_MODE.NONE}
                      onChange={() => setInitialTeamMode(INITIAL_TEAM_MODE.NONE)}
                    />
                    <Form.Radio
                      label={t("app.projects.form.teamModeExisting")}
                      name="initialTeamMode"
                      value={INITIAL_TEAM_MODE.EXISTING}
                      checked={form.initialTeamMode === INITIAL_TEAM_MODE.EXISTING}
                      disabled={!hasTeams}
                      onChange={() => setInitialTeamMode(INITIAL_TEAM_MODE.EXISTING)}
                    />
                    <Form.Radio
                      label={t("app.projects.form.teamModeNew")}
                      name="initialTeamMode"
                      value={INITIAL_TEAM_MODE.NEW}
                      checked={form.initialTeamMode === INITIAL_TEAM_MODE.NEW}
                      onChange={() => setInitialTeamMode(INITIAL_TEAM_MODE.NEW)}
                    />
                  </Form.Group>
                  {!hasTeams && !bootstrapLoading && (
                    <p className="project-form-hint project-form-hint--below-row">
                      {t("app.projects.form.teamModeExistingNeedTeams")}
                    </p>
                  )}
                  {isEditMode &&
                    form.initialTeamMode === INITIAL_TEAM_MODE.EXISTING &&
                    !hasLinkedTeamInSlots(form.existingTeamSlots) && (
                      <Message info size="small" className="project-form-team-empty-msg">
                        {t("app.projects.form.teamExistingEmptyEditHint")}
                      </Message>
                    )}
                </div>

                {form.initialTeamMode === INITIAL_TEAM_MODE.EXISTING && (
                  <div className="project-form-field project-form-span-12 project-form-team-slots-wrap">
                    <span className="project-form-label-block">{t("app.projects.form.linkedTeamsHeading")}</span>
                    {!(form.existingTeamSlots || []).some((s) => s.teamId) && (
                      <Message info size="small" className="project-form-team-empty-msg">
                        {t("app.projects.form.teamNoLinkedYet")}
                      </Message>
                    )}
                    {(form.existingTeamSlots || []).map((slot, slotIndex) => (
                      <div key={slot.rowKey} className="project-form-team-slot">
                        <div className="project-form-team-slot-toolbar">
                          <span className="project-form-team-slot-title">
                            {t("app.projects.form.teamSlotLabel", { n: slotIndex + 1 })}
                          </span>
                          {(form.existingTeamSlots || []).length > 1 && (
                            <Button
                              type="button"
                              basic
                              icon
                              className="project-form-team-slot-remove"
                              onClick={() => removeTeamSlotRow(slot.rowKey)}
                              title={t("app.projects.form.removeTeamSlot")}
                            >
                              <Icon name="trash" />
                            </Button>
                          )}
                        </div>
                        <div className="project-form-existing-teams-row">
                          <Dropdown
                            id={`pf-existing-team-ddl-${slot.rowKey}`}
                            selection
                            fluid
                            search
                            className="project-form-dropdown project-form-existing-teams-ddl"
                            placeholder={t("app.projects.form.selectTeamPlaceholder")}
                            options={teamOptionsForSlot(slot.rowKey)}
                            value={slot.teamId != null && slot.teamId !== "" ? String(slot.teamId) : ""}
                            onChange={(e, { value }) => setSlotTeamId(slot.rowKey, value)}
                            error={!!formErrors.existingTeamIds && slotIndex === 0}
                          />
                          <Button
                            type="button"
                            icon
                            labelPosition="left"
                            className="project-form-view-roster-btn"
                            disabled={!slot.teamId}
                            onClick={() => openTeamRosterModal(slot.teamId)}
                          >
                            <Icon name="users" />
                            {t("app.projects.form.viewTeamRosterButton")}
                          </Button>
                        </div>
                        {slot.teamId && (
                          <div className="project-form-field project-form-team-slot-members">
                            <label htmlFor={`pf-team-ddl-${slot.rowKey}`}>
                              {t("app.projects.form.initialMembers")}
                            </label>
                            <Dropdown
                              id={`pf-team-ddl-${slot.rowKey}`}
                              placeholder={t("app.projects.form.initialMembersPlaceholderExisting")}
                              fluid
                              multiple
                              search
                              selection
                              className="project-form-dropdown project-form-dropdown--team"
                              options={memberOptionsForTeamId(slot.teamId)}
                              value={slot.additionalMemberUserIds || []}
                              onChange={(e, { value }) =>
                                setSlotAdditionalMembers(slot.rowKey, value || [])
                              }
                            />
                            {!isEditMode && (
                              <p className="project-form-hint project-form-hint--after-control">
                                {t("app.projects.form.initialMembersCreateContextHint")}
                              </p>
                            )}
                            <p className="project-form-hint project-form-hint--stack">
                              {t("app.projects.form.initialMembersFromTeamHint")}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                    <Button
                      type="button"
                      basic
                      icon
                      labelPosition="left"
                      className="project-form-add-team-btn"
                      onClick={addTeamSlotRow}
                    >
                      <Icon name="plus" />
                      {t("app.projects.form.addAnotherTeam")}
                    </Button>
                    {formErrors.existingTeamIds && (
                      <Message negative size="tiny" content={formErrors.existingTeamIds} />
                    )}
                    <p className="project-form-hint project-form-hint--after-control" id="pf-team-roster-desc">
                      {t("app.projects.form.teamRowsHint")}
                    </p>
                  </div>
                )}

                {form.initialTeamMode === INITIAL_TEAM_MODE.NEW && (
                  <div className="project-form-field project-form-span-12">
                    <label htmlFor="pf-new-team-name">{t("app.projects.form.newTeamNameLabel")}</label>
                    <Form.Input
                      id="pf-new-team-name"
                      className="project-form-control"
                      value={form.newTeamName}
                      maxLength={NAME_MAX_LENGTH}
                      error={!!formErrors.newTeamName}
                      onChange={(e, { value }) => setField("newTeamName", value)}
                      placeholder={t("app.projects.form.newTeamNamePlaceholder")}
                    />
                    {formErrors.newTeamName && (
                      <Message negative size="tiny" content={formErrors.newTeamName} />
                    )}
                  </div>
                )}

                {showInitialMembers && (
                  <div className="project-form-field project-form-span-12">
                    <label htmlFor="pf-team-ddl">{t("app.projects.form.initialMembers")}</label>
                    <Dropdown
                      id="pf-team-ddl"
                      placeholder={t("app.projects.form.teamPlaceholder")}
                      fluid
                      multiple
                      search
                      selection
                      className="project-form-dropdown project-form-dropdown--team"
                      options={teamUserOptions}
                      value={form.teamUserIds || []}
                      onChange={onInitialMembersDropdownChange}
                    />
                    {form.initialTeamMode === INITIAL_TEAM_MODE.NONE &&
                      Array.isArray(form.teamUserIds) &&
                      form.teamUserIds.length > 0 && (
                        <p className="project-form-hint project-form-hint--after-control">
                          {t("app.projects.form.initialMembersLegacyHint")}
                        </p>
                      )}
                  </div>
                )}
                </div>

                <Modal
                  open={teamRosterModalOpen}
                  onClose={() => {
                    setTeamRosterModalOpen(false);
                    setTeamRosterModalTeamId(null);
                  }}
                  size="large"
                  closeIcon
                  className="project-form-team-roster-modal"
                  aria-labelledby="pf-team-roster-modal-title"
                >
                  <Modal.Header id="pf-team-roster-modal-title">
                    <span className="project-form-team-roster-modal-title-text">
                      <Icon name="users" className="project-form-team-roster-modal-title-icon" />
                      {t("app.projects.form.teamRosterModalTitle")}
                    </span>
                  </Modal.Header>
                  <Modal.Content scrolling className="project-form-team-roster-modal-content">
                    {teamRosterLoading ? (
                      <div className="project-form-team-roster-modal-loader">
                        <Loader active inline="centered" content={t("app.common.loading")} />
                      </div>
                    ) : (
                      <div className="project-form-team-roster-modal-body">
                        {teamRosterSections.length > 0 && (
                          <p className="project-form-team-roster-modal-scope">
                            {t("app.projects.form.teamRosterModalScopeHint", {
                              count: teamRosterSections.length,
                            })}
                          </p>
                        )}
                        {teamRosterSections.map((sec) => (
                          <section
                            key={String(sec.teamId)}
                            className="project-form-team-roster-modal-section"
                            aria-labelledby={`pf-tr-team-${String(sec.teamId)}`}
                          >
                            <header className="project-form-team-roster-modal-team-head" id={`pf-tr-team-${String(sec.teamId)}`}>
                              <span className="project-form-team-roster-modal-team-badge" aria-hidden>
                                <Icon name="group" />
                              </span>
                              <div className="project-form-team-roster-modal-team-meta">
                                <span className="project-form-team-roster-modal-team-kicker">
                                  {t("app.projects.form.teamRosterTeamLabel")}
                                </span>
                                <span className="project-form-team-roster-modal-team-name">{sec.teamName}</span>
                              </div>
                            </header>
                            {sec.fetchError ? (
                              <Message
                                negative
                                size="small"
                                className="project-form-team-roster-modal-msg"
                                content={String(sec.fetchError)}
                              />
                            ) : sec.rows.length === 0 ? (
                              <p className="project-form-team-roster-modal-empty">
                                {t("app.projects.form.teamRosterModalEmpty")}
                              </p>
                            ) : (
                              <div className="project-form-team-roster-table-wrap">
                                <Table unstackable size="small" className="project-form-team-roster-table">
                                  <Table.Header>
                                    <Table.Row>
                                      <Table.HeaderCell width={7}>
                                        {t("app.projects.form.teamRosterColMember")}
                                      </Table.HeaderCell>
                                      <Table.HeaderCell width={9}>
                                        {t("app.projects.form.teamRosterColEmail")}
                                      </Table.HeaderCell>
                                    </Table.Row>
                                  </Table.Header>
                                  <Table.Body>
                                    {sec.rows.map((row) => (
                                      <Table.Row key={row.key}>
                                        <Table.Cell>{row.memberName || "—"}</Table.Cell>
                                        <Table.Cell>{row.userEmail || "—"}</Table.Cell>
                                      </Table.Row>
                                    ))}
                                  </Table.Body>
                                </Table>
                              </div>
                            )}
                          </section>
                        ))}
                      </div>
                    )}
                  </Modal.Content>
                  <Modal.Actions className="project-form-team-roster-modal-actions">
                    <Button
                      type="button"
                      className="project-form-team-roster-modal-close-btn"
                      onClick={() => setTeamRosterModalOpen(false)}
                    >
                      {t("app.common.close")}
                    </Button>
                  </Modal.Actions>
                </Modal>

                <div className="project-form-actions">
                  <Button
                    type="button"
                    className="project-form-btn-cancel"
                    onClick={goBack}
                    disabled={isSubmitting}
                  >
                    {t("app.common.cancel")}
                  </Button>
                  <Button
                    type="submit"
                    icon
                    labelPosition="left"
                    className="project-form-btn-primary"
                    loading={isSubmitting}
                    disabled={isSubmitting}
                  >
                    <Icon name="check" />
                    {isEditMode ? t("app.projects.update") : t("app.projects.create")}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectFormPage;
